<template>
  <base-wrapper>
    <div class="space-y-4">
      <div class="flex items-center justify-end gap-x-2">
        <div class="self-center mr-2">
          <base-checkbox name="harga_tanpa_kebijakan" label="Harga Tanpa Kebijakan" v-model="filter.displayDefaultAreaPrice" />
        </div>
        <div class="self-center">
          <base-checkbox name="kirim_stok_promo" label="Kirim Stok Promo" v-model="filter.displayTransferPromo" @change="loadOrders" />
        </div>
        <base-input
          v-model="filter.search"
          :shadow="false"
          placeholder="Cari Kode"
          debounce
          @native-input="loadOrders"
          type="search"
        />
        <base-select
          fit
          :shadow="false"
          :options="[
            { key: 'all', label: 'Status', value: null },
            { key: 'invalid', label: 'Draft', value: false },
            { key: 'valid', label: 'Valid', value: true },
          ]"
          v-model="filter.is_valid_for_packing"
          @change="loadOrders"
        />
        <base-button :to="{ name: 'restock-sc.tambah' }"
          >Tambah Restock</base-button
        >
      </div>
      <div>
        <datatable
          :columns="columns"
          :data="orders.data"
          :scroll-x="false"
          :total="orders.meta.page.total"
          :perPage="orders.meta.page.perPage"
          :currentPage="orders.meta.page.currentPage"
          :meta="orders.meta"
          @pagechanged="onPageChanged"
        >
          <template v-slot:tbody="{ classes }">
            <tr
              v-for="(order) in orders.data"
              :key="order.id"
              :class="[
                classes.tr,
                getRowClass(order),
                'cursor-pointer bg-white hover:bg-green-100',
              ]"
              @click="
                onClickRow(
                  getSingleIncluded(orders, order.relationships.order.data.id)
                )
              "
            >
              <td :class="[classes.td]">
                <order-code-label :order="order" />
                <order-date-label
                  :order="order"
                  class="text-xs text-gray-500"
                />
              </td>
              <td :class="[classes.td]">
                {{ order.attributes.reference_code ?? '-' }}
              </td>
              <td :class="classes.td">
                {{
                  getSingleIncluded(orders, order.relationships.order.data.id)
                    .attributes.origin_warehouse_code
                }}
                /
                {{
                  getSingleIncluded(orders, order.relationships.order.data.id)
                    .attributes.destination_office_code
                }}
              </td>
              <td :class="[classes.td, 'text-right']">
                {{
                  getSingleIncluded(orders, order.relationships.order.data.id)
                    .attributes[filter.displayDefaultAreaPrice ? 'default_area_price' : 'grand_total_price'] | toCurrency
                }}
              </td>
              <td :class="[classes.td, 'text-center']" :title="getSingleIncluded(orders, order.relationships.order.data.id)
                .attributes.latest_cancel_description">
                <base-badge color="yellow">{{
                  order.attributes.order_status
                  }}</base-badge>
              </td>
              <td :class="[classes.td, 'text-center']">
                <base-badge color="indigo">{{
                  order.attributes.warehouse_status
                }}</base-badge>
              </td>
            </tr>
          </template>
        </datatable>
      </div>
    </div>
    <view-order-modal
      :visible="viewOrderModal.visible"
      :order-id="viewOrderModal.orderId"
      with-bonus
      :display-default-area-price="filter.displayDefaultAreaPrice"
      edit-route-name="restock-sc.edit"
      :order-detail-columns="['code', 'qty', 'price', 'total_price', 'packed_qty', 'unpacked_qty']"
      @close="viewOrderModal.visible = false"
      @validated="onValidated"
      @deleted="onDeleted"
    />
    <loading v-if="loading" />
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import { mapActions } from 'vuex';
import ViewOrderModal from '@/components/order/view-order-modal.vue';
import OrderDateLabel from '@/components/order/order-date-label.vue';
import OrderCodeLabel from '@/components/order/order-code-label.vue';
import BaseCheckbox from '../../../../components/base/BaseCheckbox.vue';

export default {
  mixins: [requestMixin],
  components: { ViewOrderModal, OrderDateLabel, OrderCodeLabel, BaseCheckbox },
  data() {
    return {
      filter: {
        is_valid_for_packing: null,
        search: null,
        displayDefaultAreaPrice: false,
        displayTransferPromo: false
      },
      orders: {
        data: [],
        meta: {
          page: {
            total: 0,
          },
        },
      },
      loading: false,
      viewOrderModal: {
        visible: false,
        orderId: null,
      },
    };
  },
  computed: {
    columns() {
      return [
        { id: 'code', name: 'No. Faktur' },
        { id: 'reference_code', name: 'No. Restock' },
        { id: 'office_code', name: 'Kode Gudang / SC' },
        {
          id: 'grand_total_price',
          name: 'Total Harga',
          theadClass: 'text-right',
        },
        {
          id: 'order_status',
          name: 'Status Restock',
          theadClass: 'text-center',
        },
        {
          id: 'warehouse_status',
          name: 'Status Order',
          theadClass: 'text-center',
        },
      ];
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    getRowClass(simpleOrder) {
      const order = this.getSingleIncluded(
        this.orders,
        simpleOrder.relationships['order'].data.id
      )

      if (!order.attributes.default_area_price) {
        return 'bg-white'
      }

      if (order.attributes.default_area_price === order.attributes.grand_total_price) {
        return 'bg-white'
      }

      return 'bg-yellow-50'
    },
    async loadOrders(params = {}) {
      this.loading = true;

      const [res, error] = await this.request('/api/v1/orders', {
        params: {
          'page[size]': 5,
          'filter[destination_office_category_id]': 3,
          include: 'order',
          'filter[search]': this.filter.search,
          ...(this.filter.is_valid_for_packing === null
            ? {}
            : this.filter.is_valid_for_packing === true
            ? { 'filter[not-order_status]': 'Draft' }
            : { 'filter[order_status]': 'Draft' }),
          'fields[simple-orders]':
            'orderedAt,order_type,reference_code,order_id,order,origin_code,destination_code,createdAt,order_status,warehouse_status',
          'fields[orders]':
            'destination_office_code,default_area_price,grand_total_price,origin_warehouse_code,is_valid_for_packing,latest_cancel_description',
          ...(this.filter.displayTransferPromo ? {} : { 'filter[is_all_transfer_promo]': false }),
          'filter[is_group_shipment]': false,
          ...params,
        },
      });

      if (!error) {
        this.orders = res;
      }

      this.loading = false;
    },
    onClickRow(order) {
      this.viewOrderModal.orderId = order.id;
      this.viewOrderModal.visible = true;
    },
    onPageChanged(page) {
      this.loadOrders({
        'page[number]': page,
      });
    },
    onValidated() {
      this.viewOrderModal.visible = false;
      this.loadOrders();
    },
    onDeleted() {
      this.viewOrderModal.visible = false;
      this.loadOrders();
    },
  },
  created() {
    this.loadOrders();
  },
};
</script>
